/**
 * Allocations API (TMMAPP-0006 category patch, TMMAPP-0008 post / bulk / reverse, TMMAPP-0011,
 * TMMAPP-0109 the TMMAPI-0145 model; design §8.7, MM-D-65, MM-D-113 / 114 / 125) — wire types from
 * `src/api/generated/`. A line targets a virtual account (into its Unassigned) or one of its
 * envelopes, carries an optional category, and takes exactly one of an amount, a percent (basis
 * points of the bank line's operating amount) or "the rest" (at most one per request); the server
 * resolves fixed, then percent, then the rest, and a breach answers `400 VALIDATION_FAILED` with
 * `details.resolved[]`. Editing never reverses and re-posts: inside one virtual account with the
 * total unchanged it is `PUT …/lines/{tid}/split`; changing how much the line puts into a virtual
 * account is `POST …/transactions/{tid}/virtual-accounts/{vaId}/total`. `scope: GROUP` re-categorizes
 * every allocation on the same envelope whose transaction resolves to the same planned series, else
 * the same normalized payee; `LINE` sets this entry alone. An absent category falls back to the
 * inherited one (a re-split) or the payee's default (MM-D-126); `uncategorized: true` (TMMAPI-0152)
 * clears it instead and never travels with a `categoryId`. A request body carries only the keys it
 * means (no null fill). The three allocate answers are typed (TMMAPI-0182 U2, J4: the line or bulk
 * body with `AllocationAiNotice`); bulk returns per-item outcomes. `Idempotency-Key` is minted once
 * per submit attempt. The AI notice at contribution (TMMAPI-0181 U6, `aiNoticeRequired` /
 * `aiNoticeVersion`) is handed to `aiContributionNotice`; the caller still refetches rather than
 * reading the line from the answer.
 */
import { aiContributionNotice } from './aiContributionNotice';
import { request } from './client';
import { isApiError } from './errors';
import { normalizeOwnerRegisterLine, type OwnerRegisterLine } from './transactions';
import type {
  AllocationBulkAnswer,
  AllocationLineAnswer,
  AlternateNameRequest,
  CategoryPatchRequest as WireCategoryPatchRequest,
  OwnerRegisterLine as WireOwnerRegisterLine,
  AssignBulkRequest,
  AssignRequest as WireAssignRequest,
  AssignSide as WireAssignSide,
  BulkError as WireBulkError,
  BulkResponse as WireBulkResponse,
  CategoryPatchResponse,
  LineRequest,
  SplitPart,
  TotalRequest,
} from './generated';

export type CategoryScope = 'LINE' | 'GROUP';

export interface CategoryPatchRequest {
  /** `null` clears the category. */
  categoryId: number | null;
  scope: CategoryScope;
}

export interface CategoryPatchResult {
  /** How many allocations changed (1 for `LINE`; the group's size for `GROUP`). */
  updated: number;
  changedAllocationIds: number[];
}

/** Where a part goes: a virtual account's Unassigned, or one of its envelopes (TMMAPI-0145). */
export type AllocationTarget = { virtualAccountId: number } | { envelopeId: number };

/** How much: exactly one of a signed amount, basis points (1–10000) of the base, or the rest. */
export type AllocationSlice = { amountMinor: number } | { percentBp: number } | { remainder: true };

export type AllocationLineRequest = AllocationTarget &
  AllocationSlice & {
    categoryId?: number;
    /** No category: neither the payee's default nor any other (TMMAPI-0152). Never with `categoryId`. */
    uncategorized?: true;
    note?: string;
    /** The bank line's one "Shows to members as" name (MM-D-117); every line of a request sends the same. */
    alternateName?: string;
  };

/** A re-split part inside one virtual account: an envelope, or `{ unassigned: true }`. */
export type SplitPartRequest = ({ envelopeId: number } | { unassigned: true }) &
  AllocationSlice & { categoryId?: number; uncategorized?: true };

/** One side of an assign: an envelope, or the virtual account's Unassigned. */
export type AssignSide = { envelopeId: number } | { unassigned: true };

export interface AssignRequest {
  from: AssignSide;
  to: AssignSide;
  amountMinor: number;
  note?: string;
}

export interface BulkItemError {
  code: string;
  message: string;
  details: Record<string, unknown> | null;
}

export interface BulkItemResult {
  transactionId: number;
  status: string;
  error: BulkItemError | null;
}

export interface BulkResult {
  items: BulkItemResult[];
}

/** An item's error; absent on success (a serializer may also send it as null). */
function normalizeBulkError(w: WireBulkError | null | undefined): BulkItemError | null {
  if (w === undefined || w === null) return null;
  return { code: w.code, message: w.message, details: w.details ?? null };
}

/** `POST /v1/allocations/bulk` answers `BulkResponse` (with the AI notice, J4). */
export function normalizeBulkResult(w: WireBulkResponse): BulkResult {
  return {
    items: w.items.map((item) => ({
      transactionId: item.transactionId,
      status: item.status,
      error: normalizeBulkError(item.error),
    })),
  };
}

/**
 * The server's resolved signed amounts, one per submitted line in order, when a `400
 * VALIDATION_FAILED` carries `details.resolved[]` (the parts broke I-1, the exact total or the
 * sign); null on any other failure. The SPA shows them inline in place of its previews (MM-D-125).
 */
export function resolvedAmounts(error: unknown): number[] | null {
  if (!isApiError(error)) return null;
  const backend = error.details.backend;
  if (typeof backend !== 'object' || backend === null) return null;
  const resolved = backend.resolved;
  if (!Array.isArray(resolved)) return null;
  const amounts = resolved.map((value) =>
    typeof value === 'number' && Number.isFinite(value) ? value : null,
  );
  return amounts.every((value): value is number => value !== null) ? amounts : null;
}

/** The slice as the wire takes it: exactly one of the three keys; the others are not sent. */
function sliceFields(
  slice: AllocationSlice,
): { amountMinor: number } | { percentBp: number } | { remainder: true } {
  if ('amountMinor' in slice) return { amountMinor: slice.amountMinor };
  if ('percentBp' in slice) return { percentBp: slice.percentBp };
  return { remainder: true };
}

/** The category as the wire takes it: an id, `uncategorized: true`, or neither key. */
function categoryFields(value: {
  categoryId?: number;
  uncategorized?: true;
}): { categoryId: number } | { uncategorized: true } | Record<string, never> {
  if (value.categoryId !== undefined) return { categoryId: value.categoryId };
  return value.uncategorized === true ? { uncategorized: true } : {};
}

/** A non-empty text, or no key. */
function textField<K extends string>(key: K, value: string | undefined): { [P in K]?: string } {
  return value !== undefined && value.length > 0 ? ({ [key]: value } as { [P in K]?: string }) : {};
}

export function toLineRequest(line: AllocationLineRequest): LineRequest {
  return {
    ...('envelopeId' in line
      ? { envelopeId: line.envelopeId }
      : { virtualAccountId: line.virtualAccountId }),
    ...sliceFields(line),
    ...categoryFields(line),
    ...textField('note', line.note),
    // An empty bank-line name is left unset.
    ...textField('alternateName', line.alternateName),
  };
}

export function toSplitPart(part: SplitPartRequest): SplitPart {
  return {
    ...('envelopeId' in part ? { envelopeId: part.envelopeId } : { unassigned: true }),
    ...sliceFields(part),
    ...categoryFields(part),
  };
}

function toAssignSide(value: AssignSide): WireAssignSide {
  return 'envelopeId' in value ? { envelopeId: value.envelopeId } : { unassigned: true };
}

function toAssignRequest(body: AssignRequest): WireAssignRequest {
  return {
    from: toAssignSide(body.from),
    to: toAssignSide(body.to),
    amountMinor: body.amountMinor,
    ...textField('note', body.note),
  };
}

export const allocationsApi = {
  /**
   * PATCH /v1/allocations/transactions/{transactionId}/alternate-name — names the whole bank line for
   * everyone but its owner (MM-D-117); `null` clears it. Answers the owner register line; a
   * card-, IBAN-, email- or account-number-shaped name is `400 VALIDATION_FAILED` on `alternateName`.
   */
  async setAlternateName(
    transactionId: number,
    alternateName: string | null,
  ): Promise<OwnerRegisterLine> {
    const json: AlternateNameRequest = { alternateName };
    return normalizeOwnerRegisterLine(
      await request<WireOwnerRegisterLine>(
        'PATCH',
        `/v1/allocations/transactions/${transactionId}/alternate-name`,
        { json },
      ),
    );
  },
  /** PATCH /v1/allocations/{id}/category → `{ updated, line }`. */
  async patchCategory(
    allocationId: number,
    body: CategoryPatchRequest,
  ): Promise<CategoryPatchResult> {
    const json: WireCategoryPatchRequest = { categoryId: body.categoryId, scope: body.scope };
    const wire = await request<CategoryPatchResponse>(
      'PATCH',
      `/v1/allocations/${allocationId}/category`,
      { json },
    );
    return { updated: wire.updated, changedAllocationIds: wire.changedAllocationIds };
  },
  /**
   * POST /v1/me/accounts/{accountId}/transactions/{transactionId}/allocations.
   * The answer is the written line with the AI notice; the caller refetches the transaction after
   * a 2xx.
   * `meetingOccurrenceId` is sent only when the dialog is opened from a session (TMMAPP-0014).
   */
  async post(
    accountId: number,
    transactionId: number,
    body: { lines: AllocationLineRequest[]; meetingOccurrenceId?: number },
    idempotencyKey: string,
  ): Promise<void> {
    const json: { lines: LineRequest[]; meetingOccurrenceId?: number } = {
      lines: body.lines.map(toLineRequest),
    };
    if (body.meetingOccurrenceId !== undefined) json.meetingOccurrenceId = body.meetingOccurrenceId;
    const answer = await request<AllocationLineAnswer>(
      'POST',
      `/v1/me/accounts/${accountId}/transactions/${transactionId}/allocations`,
      { json, idempotencyKey },
    );
    aiContributionNotice.report(answer);
  },
  /**
   * POST /v1/allocations/bulk — per-item outcomes. The register is still refetched after a 2xx;
   * the parsed `{ items }` is what the bulk sheet renders.
   */
  async bulk(
    body: { items: { transactionId: number; lines: AllocationLineRequest[] }[] },
    idempotencyKey: string,
  ): Promise<BulkResult> {
    const answer = await request<AllocationBulkAnswer>('POST', '/v1/allocations/bulk', {
      json: {
        items: body.items.map((item) => ({
          transactionId: item.transactionId,
          lines: item.lines.map(toLineRequest),
        })),
      },
      idempotencyKey,
    });
    aiContributionNotice.report(answer);
    return normalizeBulkResult(answer);
  },
  /**
   * POST /v1/allocations/transactions/{transactionId}/virtual-accounts/{virtualAccountId}/total —
   * sets how much of the line this virtual account holds (TMMAPI-0145 item 4b). A reduction comes
   * out of Unassigned first; the body is one slice plus an optional category. The answer is the line
   * with the AI notice; the caller refetches.
   */
  async setVirtualAccountTotal(
    transactionId: number,
    virtualAccountId: number,
    body: AllocationSlice & { categoryId?: number; uncategorized?: true; note?: string },
    idempotencyKey: string,
  ): Promise<void> {
    const json: TotalRequest = {
      ...sliceFields(body),
      ...categoryFields(body),
      ...textField('note', body.note),
    };
    const answer = await request<AllocationLineAnswer>(
      'POST',
      `/v1/allocations/transactions/${transactionId}/virtual-accounts/${virtualAccountId}/total`,
      { json, idempotencyKey },
    );
    aiContributionNotice.report(answer);
  },
  /**
   * PUT /v1/virtual-accounts/{virtualAccountId}/lines/{transactionId}/split — the line's desired
   * live parts inside this virtual account (TMMAPI-0145 item 6). The sum must equal the line's
   * current total there (`409 SPLIT_TOTAL_CHANGED`, `details.currentTotalMinor`). It answers the
   * ledger row; the caller refetches rather than parsing it.
   */
  async split(
    virtualAccountId: number,
    transactionId: number,
    parts: SplitPartRequest[],
  ): Promise<void> {
    await request<unknown>(
      'PUT',
      `/v1/virtual-accounts/${virtualAccountId}/lines/${transactionId}/split`,
      { json: parts.map(toSplitPart) },
    );
  },
  /** POST /v1/virtual-accounts/{id}/assign — moves money between envelopes and Unassigned (MM-D-114). */
  async assign(
    virtualAccountId: number,
    body: AssignRequest,
    idempotencyKey: string,
  ): Promise<void> {
    await request<void>('POST', `/v1/virtual-accounts/${virtualAccountId}/assign`, {
      json: toAssignRequest(body),
      idempotencyKey,
    });
  },
  /** POST /v1/virtual-accounts/{id}/assign/bulk — up to 50 assigns, all or nothing. */
  async assignBulk(
    virtualAccountId: number,
    lines: AssignRequest[],
    idempotencyKey: string,
  ): Promise<void> {
    await request<void>('POST', `/v1/virtual-accounts/${virtualAccountId}/assign/bulk`, {
      json: { lines: lines.map(toAssignRequest) } satisfies AssignBulkRequest,
      idempotencyKey,
    });
  },
  /**
   * POST /v1/allocations/{id}/reverse. The write body is opaque; the caller refetches after a 2xx.
   */
  async reverse(
    allocationId: number,
    body: { note?: string },
    idempotencyKey: string,
  ): Promise<void> {
    const json: { note?: string } = {};
    if (body.note !== undefined && body.note.length > 0) json.note = body.note;
    await request<void>('POST', `/v1/allocations/${allocationId}/reverse`, {
      json,
      idempotencyKey,
    });
  },
};
